// app/helpers/fabricSetup.js
export const initFabricCanvas = async (canvasEl, size, handlers = {}) => {
  const mod = await import("fabric");
  const fabric = mod.fabric ?? mod;

  // dispose if already has a fabric instance
  if (canvasEl.__fabric) {
    try {
      canvasEl.__fabric.dispose();
    } catch {}
    canvasEl.__fabric = null;
  }

  // ensure DOM canvas internal size matches
  canvasEl.width = size.width;
  canvasEl.height = size.height;

  const fabricCanvas = new fabric.Canvas(canvasEl, {
    width: size.width,
    height: size.height,
    selection: false,
    preserveObjectStacking: true,
  });

  canvasEl.__fabric = fabricCanvas;

  // ---- Fabric mouse / wheel (mouse + single-touch)
  const {
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onWheel,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  } = handlers;

  if (onMouseDown) {
    fabricCanvas.on("mouse:down", (opt) => {
      const p = fabricCanvas.getPointer(opt.e);
      onMouseDown(opt.e, { x: p.x, y: p.y });
    });
  }
  if (onMouseMove) {
    fabricCanvas.on("mouse:move", (opt) => {
      const p = fabricCanvas.getPointer(opt.e);
      onMouseMove(opt.e, { x: p.x, y: p.y });
    });
  }
  if (onMouseUp) {
    fabricCanvas.on("mouse:up", () => onMouseUp());
  }

  if (onWheel) {
    // Add a DOM-level listener with passive: false
    fabricCanvas.upperCanvasEl.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = fabricCanvas.upperCanvasEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        onWheel({ e, pointer: { x, y } });
      },
      { passive: false }
    );
  }

  // ---- Native touch listeners (upper canvas eats the events)
  const upper = fabricCanvas.upperCanvasEl;
  if (upper) {
    upper.style.background = "transparent";
    fabricCanvas.lowerCanvasEl.style.background = "transparent";

    if (onTouchStart) {
      upper.addEventListener(
        "touchstart",
        (e) => {
          e.preventDefault(); // needed for pinch zoom / custom gestures
          const p = fabricCanvas.getPointer(e);
          onTouchStart(e, p && { x: p.x, y: p.y });
        },
        { passive: false }
      );
    }
    if (onTouchMove) {
      upper.addEventListener(
        "touchmove",
        (e) => {
          e.preventDefault();
          const p = fabricCanvas.getPointer(e);
          onTouchMove(e, p && { x: p.x, y: p.y });
        },
        { passive: false }
      );
    }
    if (onTouchEnd) {
      upper.addEventListener(
        "touchend",
        (e) => {
          e.preventDefault();
          onTouchEnd(e);
        },
        { passive: false }
      );
    }
  }

  // return both to the caller
  return { canvas: fabricCanvas, fabric };
};

export const loadBackgroundImage = async (
  fc,
  fabricApi,
  image,
  transforms,
  displaySize, // the box you want (e.g. 600x600)
  onCanvasSizeChange // we will report the FITTED size (cw x ch)
) => {
  if (!fc || !fc.lowerCanvasEl) {
    console.error("Fabric canvas not initialized");
    return;
  }

  const img = await fabricApi.Image.fromURL(image.src, {
    crossOrigin: "anonymous",
  });

  const iw = img.width;
  const ih = img.height;
  if (!iw || !ih) {
    console.error("Image failed to load dimensions");
    return;
  }

  // normalize rotation
  const angle = (((transforms?.rotation ?? 0) % 360) + 360) % 360;

  // 1) choose your static "box"
  const boxW = displaySize?.width ?? 600;
  const boxH = displaySize?.height ?? 600;

  // 2) fit image into that box (contain). do NOT upscale small images unless you want to
  let s = Math.min(boxW / iw, boxH / ih);
  // comment the next line if you *do* want to upscale tiny images to fill the box
  s = Math.min(s, 1);

  // fitted image size inside the box
  const cw = Math.round(iw * s);
  const ch = Math.round(ih * s);

  // 3) make the canvas backstore exactly the fitted image size
  if (typeof fc.setDimensions === "function") {
    fc.setDimensions({ width: cw, height: ch });
  } else {
    fc.setWidth(cw);
    fc.setHeight(ch);
  }

  // let React/parent know (used by <canvas width/height> and CSS)
  onCanvasSizeChange?.({ width: cw, height: ch });

  // 4) place background image at (0,0), no centering, just the exact fit
  img.set({
    originX: "left",
    originY: "top",
    left: 0,
    top: 0,
    selectable: false,
    evented: false,
    angle,
    scaleX: s,
    scaleY: s,
  });

  fc.set("backgroundImage", img);
  fc.requestRenderAll();

  return img;
};
